import axios from 'axios'
import store from '../store'
import { isEmptyObject } from '@/common'
import { timestampToTime, getDateStr } from '@/common/getDateStr'
import JSONBig from 'json-bigint'
import { getCookie, deleteCookie } from '@/common/cookie'
import { message, Modal } from 'ant-design-vue'
import i18n from '@/utils/lang'

const Axios = axios.create({
  method: 'POST',
  // timeout: 10000, //接口超时加上10s的限制
  withCredentials: true,
  transformResponse: [
    (data) => {
      return JSON.stringify(JSONBig.parse(data))
    },
  ],
})

// =============== 请求防重复处理 =================
const pendingRequests = new Map()

// 自动取消重复请求的接口白名单（只在这些接口启用，防止误杀如 Promise.all(保存报价) 的并发请求）
// 后续如果别的接口也由于参数不同导致覆盖问题，往这个数组里加 URL 关键字即可
const cancelWhiteList = []

function getRequestKey(config) {
  // 忽略查询参数，保留真正的路由路径和方法（确保参数不同时也是同一个 key，这样新请求才会取消旧的）
  const url = config.url ? config.url.split('?')[0] : ''
  return `${config.method}:${url}`
}

function removePendingRequest(config) {
  if (!config.url) return

  // 手动开启了 cancelDuplicate 或者 URL 在白名单里，才执行取消逻辑
  const matchesWhiteList = cancelWhiteList.some((item) => config.url.includes(item))
  if (config.cancelDuplicate || matchesWhiteList) {
    const key = getRequestKey(config)
    if (pendingRequests.has(key)) {
      const source = pendingRequests.get(key)
      source.cancel('canceled') // 中断上一个未完成的相同请求
      pendingRequests.delete(key)
    }
  }
}

/**
 * 判断是否是时期格式
 * @param {string} date
 */
const isDate = (date) => {
  let is_date = false
  let pattern_zh = new RegExp('[\u4E00-\u9FA5]+') //判断是否有中文
  let pattern_en = new RegExp('[A-Za-z]+') //判断是否有英文
  let pattern_date = new RegExp(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/) //判断2020-10-10 10:10:10 是否存在
  if (
    !pattern_zh.test(date) &&
    !pattern_en.test(date) &&
    pattern_date.test(date) &&
    isNaN(date) &&
    !isNaN(Date.parse(date)) &&
    (date.length === 10 || date.length === 19)
  ) {
    is_date = true
  }
  return is_date
}

/**
 * 时区处理 请求加1小时，返回减1小时
 * @param {date} date
 * @param {string} type req请求 res返回
 */
const handleDate = (date, type) => {
  let timezoneDiffBeijing = localStorage.getItem('timezoneDiffBeijing') || 0
  let time = +new Date(date) + timezoneDiffBeijing * 3600 * 1000 * (type === 'req' ? 1 : -1)
  return timestampToTime(time)
}

/**
 * 时区处理 请求加1小时，返回减1小时
 * @param {object} data 对象
 * @param {string} type req请求 res返回
 */
const timeZoneProcessing = (data, type = 'req') => {
  if (isEmptyObject(data) || !data) {
    return data
  }
  Object.keys(data).forEach((item) => {
    if (typeof data[item] === 'object') {
      timeZoneProcessing(data[item], type)
    } else if (isDate(data[item])) {
      data[item] = handleDate(data[item], type)
    }
  })
  return data
}

Axios.interceptors.request.use(
  (config) => {
    // loding
    if (config.hasLoading) {
      store.commit('setState', {
        loading: true,
      })
    }

    //method
    config.method = config.type || 'post'

    // path
    let path = config.url

    if (path.startsWith('null')) {
      path = path.replace('null', localStorage.getItem('apiURL'))
    }

    //requestId=rqname+用户名+当前时间(20230906120542)+4位guid()
    let requestId = guid()
    if (path.indexOf('?') > 0) {
      path = path + '&requestId=' + requestId
    } else {
      path = path + '?requestId=' + requestId
    }
    const userSession = store.state.userSession
    // partyId
    let partyId = userSession?.currentBu?.id || userSession?.buList?.[0]?.id

    if (partyId) {
      path = path + '&cpid=' + partyId
    }
    let language = localStorage.getItem('lang') || 'zh'
    let language_mapping = {
      zh: 'zh_CN',
      en: 'en_US',
      th: 'th_TH',
    }
    path += '&language=' + language_mapping[language]
    let token = getCookie('d_session_id')
    config.headers['Authorization'] = token
    config.headers['Content-Type'] = config.contentType ?? 'application/json; charset=UTF-8'

    config.data = {
      ...(config.data || {}),
      merchantCode: localStorage.getItem('merchantCode'),
    }

    if (config.type?.toLocaleUpperCase() === 'GET') {
      for (let item in config.data) {
        path += `&${item}=${config.data[item]}`
      }
    }

    let data =
      config.data === undefined
        ? JSON.stringify({})
        : config.contentType === false
        ? config.data
        : JSON.stringify(config.data)
    config.data =
      data instanceof FormData ? data : JSON.stringify(timeZoneProcessing(JSON.parse(data), 'req'))

    config.url = path

    // 尝试取消之前的重复请求
    removePendingRequest(config)

    // 如果开启了防重复，则为当前请求创建一个新的 abort 控制器
    const matchesWhiteList = cancelWhiteList.some((item) => config.url && config.url.includes(item))
    if (config.cancelDuplicate || matchesWhiteList) {
      // 为当前请求创建一个新的 cancelToken
      const source = axios.CancelToken.source()
      config.cancelToken = source.token
      config._cancelSource = source // 将 source 挂载到配置上，方便后续移除时比对
      pendingRequests.set(getRequestKey(config), source)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

Axios.interceptors.response.use(
  (res) => {
    if (res.config.hasLoading) {
      store.commit('setState', {
        loading: false,
      })
    }

    // 请求成功后，从池中移除，释放内存
    if (res?.config) {
      const matchesWhiteList = cancelWhiteList.some(
        (item) => res.config.url && res.config.url.includes(item)
      )
      if (res.config.cancelDuplicate || matchesWhiteList) {
        const key = getRequestKey(res.config)
        // [修复核心]：只有当 Map 里的 source 还是我当前的 source 时，才删除。
        if (pendingRequests.get(key) === res.config._cancelSource) {
          pendingRequests.delete(key)
        }
      }
    }

    if (res.config.responseType === 'blob' || res.data instanceof Blob) {
      if (res.data.type && res.data.type.includes('application/json')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const value = timeZoneProcessing(JSON.parse(reader.result), 'res')
              if (value.code == 200 || value.code == 1000000) {
                if (res.config.success && typeof res.config.success === 'function') {
                  res.config.success(value)
                }
                resolve(value)
              } else {
                let errMsg = value.subMsg || value.msg || value.message
                if (errMsg) {
                  message.error(errMsg)
                }
                if (res.config.fail && typeof res.config.fail === 'function') {
                  res.config.fail(value)
                }
                reject(value)
              }
            } catch (e) {
              reject(e)
            }
          }
          reader.readAsText(res.data)
        })
      }
      if (res.config.success && typeof res.config.success === 'function') {
        res.config.success(res.data, res)
      }
      return res.data
    }

    const value = timeZoneProcessing(JSON.parse(res.data), 'res')
    // console.log('value: ', value)
    if (value.code == 200 || value.code == 1000000) {
      //为了兼容之前的http写法
      if (res.config.success && typeof res.config.success === 'function') {
        res.config.success(value)
      }
      return value
    }

    if (+value.error_code === 5001) {
      //登录超时
      goToLogin()
      return Promise.reject(value)
    }

    if (res.config.fail && typeof res.config.fail === 'function') {
      res.config.fail(value)
      return Promise.reject(value)
    }

    let errMsg = value.subMsg || value.msg || value.message
    if (errMsg) {
      message.error(errMsg)
    }
    return Promise.reject(value)
  },
  (error) => {
    store.commit('setState', {
      loading: false,
    })

    // 请求失败或被取消后，从池中移除
    if (error?.config) {
      const matchesWhiteList = cancelWhiteList.some(
        (item) => error.config.url && error.config.url.includes(item)
      )
      if (error.config.cancelDuplicate || matchesWhiteList) {
        const key = getRequestKey(error.config)
        // [修复核心]：只有当 Map 里的 source 是我当前的 source 时，我才执行清除
        if (pendingRequests.get(key) === error.config._cancelSource) {
          pendingRequests.delete(key)
        }
      }
    }

    // 特殊处理被主动取消的请求，绕过全局的报错提示弹窗，直接 reject
    if (axios.isCancel(error) || error.name === 'CanceledError' || error.message === 'canceled') {
      console.log('请求由于产生新请求而取消:', error)
      return Promise.reject(error)
    }

    let response = error.response
    console.log('error: ', response)
    try {
      if (response.config.fail && typeof response.config.fail === 'function') {
        response.config.fail(JSON.parse(response.data))
        return Promise.reject(JSON.parse(response.data))
      }
    } catch (err) {
      console.log(err)
    }

    //请求超时
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      Modal.warning({
        title: i18n.t('请求超时'),
        content: error.config.url + i18n.t('接口请求超时'),
      })
      return
    }

    try {
      let value = JSON.parse(response?.data)
      if (+value.error_code === 5001) {
        //登录超时
        goToLogin()
        return
      }
      let err = value.msg || value.subMsg || value.message
      if (err && err !== 'null') {
        if (value.path) {
          err += value.path
        }
        message.error(err)
        return Promise.reject(err)
      }
    } catch (err) {
      console.log(err)
      return Promise.reject(err)
    }
  }
)

function goToLogin() {
  Modal.destroyAll()
  Modal.confirm({
    title: i18n.t(`登录过期,请重新登录`),
    okText: i18n.t(`重新登录`),
    cancelText: i18n.t(`取消`),
    onOk: () => {
      deleteCookie(() => {
        window.location.href = '/login'
        localStorage.setItem('pageCacheFullPath', location.pathname)
      })
    },
  })
}

function guid() {
  let time = getDateStr(0, true).replace(/\D/g, '')
  let userAccount = localStorage.getItem('userAccount') || 'xxx'
  var s4 = function () {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)
  }
  return `rqname${userAccount}${time}` + s4()
}

export default Axios
